import { Avatar, Button, Stack, SxProps, Typography } from '@mui/material';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useFormContext } from 'react-hook-form';
import { ITherapySuppliers } from 'Interfaces/therapySuppliers';
import { useContext, useRef, useState } from 'react';
import {
  TherapySuppliersContext,
  defaultTherapyFilters,
} from 'Context/SuppliersContext/TherapySuppliersContext';
import {
  SUPPLIERS_RESOURCES,
  SUPPLIERS_STEPS,
  SUPPLIER_RESULT_FILTER,
} from 'Enum/suppliers';
import { AddInputs, LeftArrowWhite, RemoveInputs } from 'Images';
import {
  CaregiverGender,
  CaregiverViaInstitute,
  IsAccessible,
  Languages,
  Locations,
  Specialization,
  TherapistsName,
  TreatmentMethods,
} from './Inputs';
import { AllFiltersModalButtons } from './AllFiltersModalButtons';
import { useNavigate } from 'react-router-dom';
import { SuppliersContext } from 'Context/SuppliersContext';
import { ORDER_BY } from '../../../ResultsPages/Header/DynamicAdvancedFilter/AllFilters/Form/OrderSort';
import Expertise from './Inputs/SubSpecialization';
import { Loader } from 'Components';

interface IForm {
  formSx?: SxProps;
  callback?: Function;
  selectedOrderBy?: number;
}

export const Form = ({ formSx, callback, selectedOrderBy }: IForm) => {
  const { handleSubmit, setValue, watch, reset } =
    useFormContext<ITherapySuppliers>();
  const { stepIndex, setStepIndex } = useContext(SuppliersContext);
  const { setFilters, filters, dispatchSuppliers, isLoading, resources } =
    useContext(TherapySuppliersContext);
  const [isExpanded, setIsExpanded] = useState(
    stepIndex === SUPPLIERS_STEPS.RESULTS
  );

  const cities = useAppSelector(
    ({ generalOptions: { generalOptions } }) => generalOptions
  )?.cities;

  const navigate = useNavigate();

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Suppliers
  ).therapySearchForm;

  const isDesktop = useIsDesktop();

  const isFormStep = stepIndex === SUPPLIERS_STEPS.FORM;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if ((watch('instituteId') as any) === '') setValue('instituteId', null);

    await handleSubmit((data) => {
      const updatedFilter = {
        ...defaultTherapyFilters,
        accessible: data.accessible === true ? true : null,
        area: data.area,
        city: data.city,
        region: data.region,
        specialty: data.specialty,
        expertise: data.expertise,
        supName: data.supName,
        gender: isExpanded ? data.gender : null,
        treatMethod: isExpanded ? data.treatMethod : [],
        lng: isExpanded ? data.lng : [],
        homeVisit: data.accessible === false ? true : null,
        instituteId: isExpanded ? data.instituteId : null,
        orderByAscendingAlphabet: selectedOrderBy === ORDER_BY.ASCENDING,
        orderByDescendingAlphabet: selectedOrderBy === ORDER_BY.DESCENDING,
        location: null,
      };
      setFilters(updatedFilter);
      setStepIndex(SUPPLIERS_STEPS.RESULTS);
      navigate('/suppliers/therapy/results');
      if (callback) {
        callback();
        dispatchSuppliers({
          type: SUPPLIER_RESULT_FILTER.Therapy,
          payload: {
            value: updatedFilter,
          },
        });
      }
    })();
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 250,
      behavior: 'smooth',
    });
  };

  const isSpecialtyReset = useRef<boolean>(false);

  const isLocationReset = useRef<boolean>(false);

  const isTherapistsNameReset = useRef<boolean>(false);

  if (!resources[SUPPLIERS_RESOURCES.profession]) return <Loader />;

  return (
    <Stack
      sx={{
        maxWidth: '770px',
        padding: isDesktop ? '16px 24px 48px' : '16px 16px 48px',
        alignItems: 'center',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'primary.shade_100',
        backgroundColor: 'white',
        boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
        m: '16px 0 24px',
        ...formSx,
      }}
      component='form'
      onSubmit={onSubmit}
    >
      {isFormStep && (
        <Button
          onClick={() => {
            reset({
              ...defaultTherapyFilters,
              accessible: null,
              gender: null,
            });
            isSpecialtyReset.current = true;
            isLocationReset.current = true;
            isTherapistsNameReset.current = true;
            setTimeout(() => {
              setIsExpanded(false);
            }, 600);
          }}
          sx={{ alignSelf: 'flex-end', p: '2px 0 0' }}
        >
          <Typography sx={{ textDecorationLine: 'underline' }}>
            {dictionary.reset}
          </Typography>
        </Button>
      )}

      <Stack gap='40px' maxWidth='400px'>
        {isFormStep && <Specialization isReset={isSpecialtyReset} />}
        {isLoading ? (
          <Loader scale='50' sx={{ height: '100px' }} size={50} />
        ) : (
          <Expertise />
        )}
        <Locations
          options={cities || []}
          name='city'
          placeholder={dictionary.cityPlaceholder}
        />
        <IsAccessible />
        <TherapistsName isReset={isTherapistsNameReset} />
        <Stack
          height={isExpanded ? undefined : '0px'}
          sx={{
            transition: 'all .5s',
            overflow: isExpanded ? 'visible' : 'hidden',
            gap: '40px',
          }}
        >
          {isExpanded && (
            <>
              <Languages />
              <CaregiverGender />
              <TreatmentMethods />
              <CaregiverViaInstitute />
            </>
          )}
        </Stack>
        {stepIndex === SUPPLIERS_STEPS.FORM && (
          <Button
            variant='text'
            onClick={() => {
              isExpanded && scrollToTop();
              setTimeout(
                () => {
                  setIsExpanded(!isExpanded);
                  if (!isExpanded) {
                    const languageButton = document.getElementsByClassName(
                      'MuiSelect-select MuiSelect-outlined MuiSelect-multiple MuiOutlinedInput-input MuiInputBase-input MuiInputBase-inputSizeSmall'
                    )[filters.specialty ? 1 : 0];
                    languageButton && (languageButton as any)?.focus();
                  }
                },
                isExpanded ? 600 : 0
              );
            }}
            sx={{ gap: '8px' }}
          >
            <Avatar
              src={isExpanded ? RemoveInputs : AddInputs}
              alt=''
              sx={{ height: '28px', width: '28px' }}
            />
            <Typography variant='body5' color='#1552E8'>
              {isExpanded
                ? dictionary.lessSearchOptions
                : dictionary.moreSearchOptions}
            </Typography>
          </Button>
        )}
      </Stack>
      {isFormStep ? (
        <Button
          variant='contained'
          sx={{ height: '46px', width: '240px', gap: '4px', mt: '64px' }}
          type='submit'
        >
          <Typography variant='body4'>{dictionary.search}</Typography>
          <Avatar
            alt=''
            src={LeftArrowWhite}
            sx={{ height: '20px', width: '20px' }}
          />
        </Button>
      ) : (
        <AllFiltersModalButtons
          resetCallback={() => {
            reset({
              ...defaultTherapyFilters,
              accessible: null,
              gender: null,
              specialty: filters.specialty,
            });
            isSpecialtyReset.current = true;
            isLocationReset.current = true;
            isTherapistsNameReset.current = true;
            scrollToTop();
            setTimeout(() => {
              setIsExpanded(false);
            }, 600);
          }}
        />
      )}
    </Stack>
  );
};
